/**
 * PlatformOrgsPage (Vincent #173, Eric 2026-05-25)
 *
 * Page ROOT_OWNER-only listant TOUTES les organisations de la plateforme avec
 * un bouton "Supprimer définitivement" pour purger en cascade.
 *
 * Double confirmation (tape le nom exact de l'org) pour éviter l'erreur fatale.
 * Orgs protégées (Knoria Plateforme, Finance Libre HQ, 9439-7452) ont le bouton désactivé.
 */
import { useCallback, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Trash,
  Lock,
  Buildings,
  Warning,
  SpinnerGap,
  Users,
  VideoCamera,
  Brain,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import SortDropdown, { sortItems } from "../components/common/SortDropdown";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlatformOrgsPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [orgs, setOrgs] = useState([]);
  const [regions, setRegions] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingRegionFor, setSavingRegionFor] = useState(null);
  const [confirmOrg, setConfirmOrg] = useState(null); // org being deleted
  const [confirmName, setConfirmName] = useState("");
  const [purging, setPurging] = useState(false);

  // Vincent #334 (Eric 2026-06-17) — Tri des organisations
  const [orgsSortBy, setOrgsSortBy] = useState("alpha_asc");
  const sortedOrgs = useMemo(() => sortItems(orgs, orgsSortBy), [orgs, orgsSortBy]);

  const isRootOwner = user?.platform_role === "ROOT_OWNER";

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const [orgsRes, regionsRes] = await Promise.all([
        axios.get(`${API}/api/platform/organizations`, { withCredentials: true }),
        axios.get(`${API}/api/organizations/data-regions`, { withCredentials: true }),
      ]);
      setOrgs(orgsRes.data.items || []);
      setRegions(regionsRes.data || {});
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          (lang === "fr" ? "Accès refusé ou erreur" : "Access denied or error")
      );
    } finally {
      setLoading(false);
    }
  }, [lang]);

  // Vincent #198 (Eric 2026-05-28) — Change la région de stockage d'une org
  // cible depuis cette liste (sans switcher d'org). Founder uniquement.
  const updateRegion = async (orgId, newRegion) => {
    setSavingRegionFor(orgId);
    try {
      await axios.put(
        `${API}/api/organizations/data-region`,
        { data_region: newRegion, org_id: orgId },
        { withCredentials: true }
      );
      const regionLabel = regions[newRegion]?.label || newRegion;
      toast.success(
        lang === "fr"
          ? `Région mise à jour → ${regionLabel}`
          : `Region updated → ${regionLabel}`
      );
      await loadOrgs();
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Échec" : "Failed"));
    } finally {
      setSavingRegionFor(null);
    }
  };

  useEffect(() => {
    if (isRootOwner) loadOrgs();
    else setLoading(false);
  }, [isRootOwner, loadOrgs]);

  // Vincent #289 (2026-06-11) — Toggle par entreprise (off par défaut) :
  // forcer l'utilisation des clés API IA de Knoria au lieu du BYOK de l'org.
  const [savingForceFor, setSavingForceFor] = useState(null);
  const toggleForceKnoriaKeys = async (org) => {
    const enabled = !org.force_knoria_keys;
    setSavingForceFor(org.id);
    try {
      await axios.put(
        `${API}/api/platform/organizations/${org.id}/force-knoria-keys`,
        { enabled },
        { withCredentials: true }
      );
      toast.success(
        enabled
          ? (lang === "fr" ? `"${org.name}" utilise maintenant les clés AI de Knoria` : `"${org.name}" now uses Knoria AI keys`)
          : (lang === "fr" ? `"${org.name}" utilise à nouveau ses propres clés BYOK` : `"${org.name}" back to its own BYOK keys`)
      );
      setOrgs((prev) => prev.map((o) => (o.id === org.id ? { ...o, force_knoria_keys: enabled } : o)));
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Échec" : "Failed"));
    } finally {
      setSavingForceFor(null);
    }
  };

  // Tâche 1303 — Bascule service par service, pour dépanner la diarisation
  // d'un cabinet sans toucher à sa transcription ni à ses courriels.
  const SERVICES_BASCULABLES = [
    { id: "assemblyai", libelle: "Diarisation", detail: "AssemblyAI" },
    { id: "openai", libelle: "Transcription", detail: "OpenAI / Whisper" },
    { id: "resend", libelle: "Courriels", detail: "Resend" },
  ];
  const [savingService, setSavingService] = useState(null);
  const toggleServiceKnoria = async (org, service) => {
    const enabled = !(org.force_knoria_keys_services || {})[service];
    setSavingService(`${org.id}:${service}`);
    try {
      const { data } = await axios.put(
        `${API}/api/platform/organizations/${org.id}/force-knoria-keys/${service}`,
        { enabled },
        { withCredentials: true }
      );
      toast.success(
        lang === "fr"
          ? `${service} de « ${org.name} » : ${enabled ? "clé Knoria" : "clé du client"}. ${data.note || ""}`
          : `${service} for "${org.name}": ${enabled ? "Knoria key" : "client key"}`,
        { duration: 8000 }
      );
      setOrgs((prev) =>
        prev.map((o) =>
          o.id === org.id
            ? { ...o, force_knoria_keys_services: { ...(o.force_knoria_keys_services || {}), [service]: enabled } }
            : o
        )
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "fr" ? "Échec de la bascule" : "Toggle failed"));
    } finally {
      setSavingService(null);
    }
  };


  const handlePurge = async () => {
    if (!confirmOrg || confirmName !== confirmOrg.name) return;
    setPurging(true);
    try {
      const { data } = await axios.delete(
        `${API}/api/platform/organizations/${confirmOrg.id}/purge`,
        {
          data: { confirm_org_name: confirmName },
          withCredentials: true,
          timeout: 60000,
        }
      );
      toast.success(
        lang === "fr"
          ? `✅ "${data.org_name}" supprimée. ${data.total_records_deleted} enregistrement(s) effacé(s).`
          : `✅ "${data.org_name}" deleted. ${data.total_records_deleted} record(s) erased.`
      );
      setConfirmOrg(null);
      setConfirmName("");
      await loadOrgs();
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          (lang === "fr" ? "Échec de la suppression" : "Delete failed")
      );
    } finally {
      setPurging(false);
    }
  };

  if (!isRootOwner) {
    return (
      <div className="p-8 text-center text-zinc-500">
        <Lock size={48} className="mx-auto mb-3 text-zinc-300" />
        {lang === "fr"
          ? "Accès strictement réservé aux ROOT_OWNER de la plateforme."
          : "Strictly reserved to platform ROOT_OWNERS."}
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Outfit'] flex items-center gap-2">
            <Buildings size={28} className="text-[#002FA7]" />
            {lang === "fr" ? "Gestion des organisations" : "Organizations management"}
          </h1>
          <p className="text-zinc-500 mt-1">
            {lang === "fr"
              ? "ROOT_OWNER uniquement. Suppression cascade définitive des orgs de test ou indésirables."
              : "ROOT_OWNER only. Cascade hard-delete of test or unwanted orgs."}
          </p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="purge-warning-banner">
        <Warning size={22} className="text-amber-600 flex-shrink-0 mt-0.5" weight="fill" />
        <div className="text-sm text-amber-900">
          <strong>{lang === "fr" ? "Action irréversible." : "Irreversible action."}</strong>{" "}
          {lang === "fr"
            ? "La suppression d'une organisation efface TOUS ses utilisateurs, vidéos, bases de connaissances, scripts, parcours, coupons, channels et messages. Cette opération est auditée (Loi 25) mais NE PEUT PAS être annulée. Les orgs principales (Knoria, Finance Libre HQ, 9439-7452) sont protégées."
            : "Deleting an org wipes ALL its users, videos, knowledge bases, scripts, pathways, coupons, channels and messages. This is audited (Law 25) but CANNOT be undone. Core orgs are protected."}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <SpinnerGap size={32} className="animate-spin text-[#002FA7]" />
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="orgs-table">
          {/* Vincent #334 (Eric 2026-06-17) — Dropdown de tri */}
          <div className="px-4 py-2 border-b border-zinc-100 bg-white flex justify-end">
            <SortDropdown value={orgsSortBy} onChange={setOrgsSortBy} testId="orgs-sort" />
          </div>
          <table className="w-full">
            <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">{lang === "fr" ? "Organisation" : "Organization"}</th>
                <th className="px-4 py-3 text-left">{lang === "fr" ? "Région" : "Region"}</th>
                <th className="px-4 py-3 text-center">
                  <Users size={14} className="inline" /> Users
                </th>
                <th className="px-4 py-3 text-center">
                  <VideoCamera size={14} className="inline" /> Vidéos
                </th>
                <th className="px-4 py-3 text-center">
                  <Brain size={14} className="inline" /> KUs
                </th>
                <th className="px-4 py-3 text-center" title={lang === "fr" ? "Forcer l'utilisation des clés API IA de Knoria au lieu du BYOK de l'entreprise — globalement, ou service par service (tâche 1303)" : "Force Knoria AI keys instead of org BYOK — globally or per service"}>
                  {lang === "fr" ? "Clés AI Knoria" : "Knoria AI keys"}
                </th>
                <th className="px-4 py-3 text-right">{lang === "fr" ? "Action" : "Action"}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 text-sm">
              {sortedOrgs.map((o) => (
                <tr key={o.id} data-testid={`org-row-${o.id}`} className={o.protected ? "bg-zinc-50/50" : ""}>
                  <td className="px-4 py-3">
                    <div className="font-medium flex items-center gap-2">
                      {o.protected && <Lock size={14} className="text-teal-600" weight="fill" />}
                      {o.name}
                    </div>
                    <div className="text-xs text-zinc-400 font-mono">{o.id}</div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={o.data_region || ""}
                      onChange={(e) => updateRegion(o.id, e.target.value)}
                      disabled={savingRegionFor === o.id}
                      className="text-xs px-2 py-1 border border-zinc-200 rounded bg-white hover:border-[#002FA7] focus:border-[#002FA7] focus:outline-none disabled:opacity-50"
                      data-testid={`region-select-${o.id}`}
                      title={lang === "fr" ? "Région de stockage des données" : "Data storage region"}
                    >
                      <option value="" disabled>—</option>
                      {Object.entries(regions).map(([key, info]) => (
                        <option key={key} value={key}>
                          {`${info.label}${info.compliance?.includes("Loi 25") ? " ✓ Loi 25" : ""}`}
                        </option>
                      ))}
                    </select>
                    {o.data_region && regions[o.data_region]?.compliance && (
                      <div className="text-[10px] text-teal-700 mt-0.5">
                        {regions[o.data_region].compliance.join(" · ")}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">{o.users_count}</td>
                  <td className="px-4 py-3 text-center">{o.videos_count}</td>
                  <td className="px-4 py-3 text-center">{o.kus_count}</td>
                  <td className="px-4 py-3 text-center">
                    {/* Vincent #289 — Toggle "Forcer les clés Knoria" (off par défaut) */}
                    <button
                      onClick={() => toggleForceKnoriaKeys(o)}
                      disabled={savingForceFor === o.id}
                      className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition-colors disabled:opacity-50 ${
                        o.force_knoria_keys
                          ? "bg-teal-100 text-teal-700 border-teal-300 hover:bg-teal-200"
                          : "bg-zinc-100 text-zinc-500 border-zinc-200 hover:bg-zinc-200"
                      }`}
                      data-testid={`force-knoria-keys-${o.id}`}
                      title={lang === "fr"
                        ? (o.force_knoria_keys
                          ? "ON — cette entreprise utilise les clés API IA de Knoria. Cliquer pour revenir à son BYOK."
                          : "OFF — cette entreprise utilise ses propres clés BYOK. Cliquer pour forcer les clés Knoria.")
                        : (o.force_knoria_keys ? "ON — using Knoria AI keys" : "OFF — using own BYOK keys")}
                    >
                      {o.force_knoria_keys ? "ON" : "OFF"}
                    </button>
                    {/* Tâche 1303 — Bascule par service. Grisée quand le
                        drapeau global est ON : il l'emporte, et un réglage
                        qu'on peut cliquer sans effet est un piège. */}
                    <div className="mt-2 flex flex-col items-center gap-1">
                      {SERVICES_BASCULABLES.map((s) => {
                        const actif = !!(o.force_knoria_keys_services || {})[s.id];
                        const neutralise = !!o.force_knoria_keys;
                        return (
                          <button
                            key={s.id}
                            onClick={() => toggleServiceKnoria(o, s.id)}
                            disabled={neutralise || savingService === `${o.id}:${s.id}`}
                            className={`w-full text-[10px] px-2 py-0.5 rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                              actif || neutralise
                                ? "bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100"
                                : "bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-100"
                            }`}
                            data-testid={`force-knoria-service-${s.id}-${o.id}`}
                            title={
                              neutralise
                                ? "Le réglage global ci-dessus est actif : il s'applique à tous les services."
                                : actif
                                  ? `${s.detail} : ce service utilise la clé de Knoria. Cliquer pour revenir à la clé du client.`
                                  : `${s.detail} : ce service utilise la clé du client. Cliquer pour basculer sur celle de Knoria.`
                            }
                          >
                            {s.libelle} {actif || neutralise ? "· Knoria" : "· client"}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link
                        to={`/dashboard/platform/organizations/${o.id}/members`}
                        className="px-2 py-1 text-xs bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded inline-flex items-center gap-1"
                        data-testid={`manage-members-${o.id}`}
                      >
                        <Users size={12} /> Membres
                      </Link>
                      {o.protected ? (
                        <span className="text-xs text-teal-700 font-medium" data-testid={`protected-${o.id}`}>
                          🔒 {lang === "fr" ? "Protégée" : "Protected"}
                        </span>
                      ) : (
                        <Button
                          onClick={() => {
                            setConfirmOrg(o);
                            setConfirmName("");
                          }}
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          data-testid={`purge-btn-${o.id}`}
                        >
                          <Trash size={14} className="mr-1" />
                          {lang === "fr" ? "Supprimer" : "Delete"}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {orgs.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-zinc-400">
                    {lang === "fr" ? "Aucune organisation." : "No organization."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation modal */}
      <Dialog open={!!confirmOrg} onOpenChange={(v) => !v && setConfirmOrg(null)}>
        <DialogContent data-testid="purge-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Warning size={22} weight="fill" />
              {lang === "fr" ? "Confirmation requise" : "Confirmation required"}
            </DialogTitle>
            <DialogDescription>
              {lang === "fr" ? (
                <>
                  Vous allez supprimer définitivement l'organisation{" "}
                  <strong>{confirmOrg?.name}</strong> et toutes ses données :
                  <ul className="list-disc list-inside mt-2 text-sm">
                    <li>{confirmOrg?.users_count} utilisateur(s)</li>
                    <li>{confirmOrg?.videos_count} vidéo(s)</li>
                    <li>{confirmOrg?.kus_count} connaissance(s)</li>
                    <li>Tous les channels, messages, scripts, parcours, coupons</li>
                  </ul>
                  Pour confirmer, tapez le nom exact de l'organisation :
                </>
              ) : (
                <>
                  You are about to permanently delete <strong>{confirmOrg?.name}</strong>{" "}
                  and all its data. To confirm, type the exact org name:
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={confirmOrg?.name || ""}
            className="font-mono"
            data-testid="purge-confirm-input"
            autoFocus
          />
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOrg(null)}
              disabled={purging}
              data-testid="purge-cancel-btn"
            >
              {lang === "fr" ? "Annuler" : "Cancel"}
            </Button>
            <Button
              onClick={handlePurge}
              disabled={purging || confirmName !== confirmOrg?.name}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="purge-confirm-btn"
            >
              {purging ? (
                <>
                  <SpinnerGap size={16} className="animate-spin mr-2" />
                  {lang === "fr" ? "Suppression…" : "Deleting…"}
                </>
              ) : (
                <>
                  <Trash size={16} className="mr-2" />
                  {lang === "fr" ? "Supprimer définitivement" : "Delete permanently"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
